import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../client/api';
import { LogoBadge } from '../components/Logo';
import { toast } from '../components/toast';
import { ConfirmModal } from '../components/menu';
import { DevAccountPanel } from '../components/DevAccountSwitcher';
import { cn } from '../utils/cn';

// =============================================================================
// Login screen (Figma '로그인') — 아이디/비밀번호 로그인.
// Google SSO는 로그인에서 제거됨 — BO 운영자 비밀번호는 서버 부트스트랩
// (BOOTSTRAP_BO_PASSWORD)이 보장한다. Google OAuth 라우트 자체는 Chat/Calendar
// 연동의 계정 연결용으로 유지된다.
//   - 아이디: 영문/숫자만 — 국문 입력 시 인라인 에러
//   - 비밀번호: 입력 시 viewable 아이콘 노출(마스킹 토글)
//   - 로그인 버튼: 미입력 시 비활성. 실패 시 토스트, 성공 시 미디어 관리 진입
//     (성공 → onAuthenticated → 게이트 re-probe → 기본 라우트 /media).
// 백엔드는 '@' 없는 입력을 login_id로 해석하므로 아이디/이메일 모두 허용.
// 둘 다 되므로 입력칸 안내도 "아이디 또는 이메일"이다(SCDS-95).
// =============================================================================

type Props = {
  /** Called after a successful password login so the gate can re-probe me. */
  onAuthenticated: () => void;
};

// 아이디 허용 문자 — 영문/숫자/이메일 기호. 한글 등 그 외 문자는 에러.
const LOGIN_ID_RE = /^[A-Za-z0-9@._+-]*$/;
const SUPPORT_EMAIL = 'support@bricklit.club';

export function Login({ onAuthenticated }: Props): JSX.Element {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [idError, setIdError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loginBlocked, setLoginBlocked] = useState(false);

  const valid = loginId.trim().length > 0 && password.length > 0 && !idError;

  function handleIdChange(v: string): void {
    setLoginId(v);
    setIdError(LOGIN_ID_RE.test(v) ? null : '영문과 숫자만 입력 가능합니다.');
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (submitting || !valid) return;
    setSubmitting(true);
    try {
      await api.auth.passwordLogin({ email: loginId.trim(), password });
      onAuthenticated();
    } catch (err) {
      if (err instanceof ApiError && err.status === 423 && err.code === 'org_pending_deletion') {
        setLoginBlocked(true);
      } else if (err instanceof ApiError && err.status === 423 && err.code === 'account_locked') {
        toast('로그인 시도가 잠겼습니다. 15분 후 다시 시도하거나 관리자에게 문의해 주세요.', 'error');
      } else if (err instanceof ApiError && err.status === 401) {
        toast('잘못된 계정정보를 입력하였습니다.', 'error');
      } else {
        toast('서버 오류가 발생하였습니다. 시스템 관리자에게 문의하세요.', 'error');
      }
      setSubmitting(false);
    }
  }

  const field =
    'w-full rounded-md border bg-panel-2 px-3.5 py-2.5 text-[15px] text-text placeholder:text-text-faint transition-colors duration-fast focus:border-accent-ring';

  return (
    <div className="flex min-h-screen items-center justify-center bg-panel p-6 text-text">
      {/* 전체 화면형 플랫 레이아웃 — 카드/보더 없이 중앙 정렬 컬럼. */}
      <div className="w-full max-w-[380px]">
        <div className="mb-10 flex flex-col items-center gap-4">
          <LogoBadge size={96} className="block" />
          <p className="text-[14px] text-text-dim">관리자 페이지</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3.5" noValidate>
          <label className="flex flex-col gap-1.5">
            <input
              type="text"
              name="username"
              autoComplete="username"
              required
              value={loginId}
              onChange={(ev) => handleIdChange(ev.target.value)}
              placeholder="아이디 또는 이메일"
              className={cn(field, idError ? 'border-red' : 'border-line')}
              disabled={submitting}
            />
            {idError && (
              <span role="alert" className="text-[12px] text-red">{idError}</span>
            )}
          </label>

          <label className="relative flex flex-col gap-1.5">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              placeholder="비밀번호"
              className={cn(field, 'border-line pr-10')}
              disabled={submitting}
            />
            {/* Figma: 입력 시 viewable 아이콘 노출 — 마스킹 토글 */}
            {password.length > 0 && (
              <button
                type="button"
                tabIndex={-1}
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-dim hover:text-text"
              >
                <EyeIcon off={showPassword} />
              </button>
            )}
          </label>

          <button
            type="submit"
            disabled={!valid || submitting}
            className={cn(
              'mt-1 w-full rounded-md bg-accent-solid px-4 py-2.5 text-[14px] font-semibold text-white transition-colors duration-fast',
              'hover:bg-accent-solid-hover disabled:cursor-not-allowed disabled:opacity-40',
            )}
          >
            {submitting ? '로그인 중…' : '로그인'}
          </button>
        </form>

        <p className="mt-4 text-center text-[12px] text-text-dim">
          아이디/비밀번호 분실 및 로그인 문의<br />
          <a href={`mailto:${SUPPORT_EMAIL}`} className="underline underline-offset-2 hover:text-text">
            {SUPPORT_EMAIL}
          </a>
        </p>
        <DevAccountPanel onAuthenticated={onAuthenticated} />
      </div>
      {loginBlocked && (
        <ConfirmModal
          title="BRICK Lit 로그인 불가"
          message={<>
            {'현재 이 계정으로 로그인 할 수 없습니다.\n자세한 사항은 BRICK Lit 관리자에게 문의해 주세요.\n\n'}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline underline-offset-2 hover:text-text">
              {SUPPORT_EMAIL}
            </a>
          </>}
          confirmLabel="확인"
          hideCancel
          danger={false}
          onCancel={() => setLoginBlocked(false)}
          onConfirm={() => setLoginBlocked(false)}
        />
      )}
    </div>
  );
}

function EyeIcon({ off }: { off: boolean }): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}
